import { useCallback } from 'react'
import type { EnrollmentStepSubmissionData } from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import type { EligibilityFormData } from '../../../../components/eligibilitySteps/types'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import {
  buildContactDetailsStepData,
  buildEligibilityStepData,
  buildLicenseDetailsStepData,
  getContactDetailsValidationError
} from './payloads'

type HandleSubmitStepFn = (
  step: number,
  data: EnrollmentStepSubmissionData,
  options?: {
    fallbackNextStep?: number
    forcedNextStep?: number
    onSuccess?: () => void
  }
) => Promise<void>

type UseEnrollmentProfileContinueHandlersArgs = {
  formData: EligibilityFormData
  handleSubmitStep: HandleSubmitStepFn
  limitToStep: number | null
  navigate: (to: string, options?: { replace?: boolean; state?: Record<string, unknown> }) => void
  setDefaultStepSaveError: (error: unknown) => void
  setStepSubmissionError: (value: string | null) => void
}

export function useEnrollmentProfileContinueHandlers({
  formData,
  handleSubmitStep,
  limitToStep,
  navigate,
  setDefaultStepSaveError,
  setStepSubmissionError
}: UseEnrollmentProfileContinueHandlersArgs) {
  const handleEligibilityContinue = useCallback(() => {
    if (formData.isCitizen === null) {
      setStepSubmissionError('Please select whether you are a U.S. citizen or permanent resident.')
      return
    }

    handleSubmitStep(
      SELF_ENROLLMENT_STEPS.ELIGIBILITY,
      buildEligibilityStepData(formData, formData.isCitizen),
      {
        fallbackNextStep: formData.isCitizen ? SELF_ENROLLMENT_STEPS.LICENSE_DETAILS : undefined,
        forcedNextStep: formData.isCitizen ? undefined : SELF_ENROLLMENT_STEPS.NOT_ELIGIBLE
      }
    ).catch(setDefaultStepSaveError)
  }, [formData, handleSubmitStep, setDefaultStepSaveError, setStepSubmissionError])

  const handleLicenseDetailsContinue = useCallback(() => {
    handleSubmitStep(
      SELF_ENROLLMENT_STEPS.LICENSE_DETAILS,
      buildLicenseDetailsStepData(formData),
      {
        fallbackNextStep: SELF_ENROLLMENT_STEPS.CONTACT_DETAILS
      }
    ).catch(setDefaultStepSaveError)
  }, [formData, handleSubmitStep, setDefaultStepSaveError])

  const handleContactDetailsContinue = useCallback(() => {
    const validationError = getContactDetailsValidationError(
      formData,
      ENROLLMENT_COMMON_MESSAGES.emergencyContactPhoneInvalid
    )
    if (validationError) {
      setStepSubmissionError(validationError)
      return
    }

    handleSubmitStep(
      SELF_ENROLLMENT_STEPS.CONTACT_DETAILS,
      buildContactDetailsStepData(formData),
      {
        fallbackNextStep:
          limitToStep === SELF_ENROLLMENT_STEPS.CONTACT_DETAILS
            ? undefined
            : SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
      }
    )
      .then(() => {
        if (limitToStep === SELF_ENROLLMENT_STEPS.CONTACT_DETAILS) {
          navigate('/thank-you', { state: { completedAdditionalInfo: true } })
        }
      })
      .catch(setDefaultStepSaveError)
  }, [formData, handleSubmitStep, limitToStep, navigate, setDefaultStepSaveError, setStepSubmissionError])

  return {
    handleContactDetailsContinue,
    handleEligibilityContinue,
    handleLicenseDetailsContinue
  }
}
